import { NavLink, useNavigate } from "react-router-dom";
import { Sprout, Crown, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { useUiStore } from "@/store/uiStore";
import { useAuth } from "@/contexts/AuthContext";
import { NAV_GROUPS, ADMIN_NAV, ACCOUNT_NAV, NavItem } from "@/shared/nav";
import { isAdminUser, canUser } from "@/shared/rbac";
import { useLabels } from "@/lib/relabel";
import { useVertical } from "@/lib/useVertical";

function NavRow({ item, collapsed, locked, onLocked }: { item: NavItem; collapsed: boolean; locked?: boolean; onLocked?: () => void }) {
  const Icon = item.icon;
  if (locked) {
    return (
      <button
        onClick={onLocked}
        data-testid={`nav-locked-${item.to.replace("/", "")}`}
        title={collapsed ? `${item.label} (upgrade)` : undefined}
        className={`group w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-white/40 hover:bg-white/5 hover:text-white/70 transition-colors ${collapsed ? "justify-center" : ""}`}
      >
        <Icon className="h-[18px] w-[18px] shrink-0" />
        {!collapsed && <span className="truncate flex-1 text-left">{item.label}</span>}
        {!collapsed && <Lock className="h-3.5 w-3.5 shrink-0 text-ai/70" />}
      </button>
    );
  }
  return (
    <NavLink
      to={item.to}
      data-testid={`nav-${item.to.replace("/", "")}`}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          collapsed ? "justify-center" : ""
        } ${
          isActive
            ? "bg-white text-forest-900 shadow-sm"
            : "text-white/70 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? "text-ai" : ""}`} />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}

export function SidebarNav() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const { user } = useAuth();
  const navigate = useNavigate();
  const admin = isAdminUser(user);
  const trialing = user?.plan_status === "trialing";
  const features = new Set<string>((user?.features as string[]) || []);
  const { nav } = useLabels();
  const { id: vertical } = useVertical();

  const goUpgrade = (feature?: string) => navigate(feature ? `/upgrade?feature=${feature}` : "/upgrade");

  return (
    <aside
      data-testid="app-sidebar"
      className={`hidden lg:flex flex-col shrink-0 bg-forest-900 text-white sticky top-0 h-screen transition-[width] duration-200 ${
        collapsed ? "w-[68px]" : "w-64"
      }`}
    >
      <div className={`flex items-center gap-3 h-16 border-b border-white/10 ${collapsed ? "justify-center px-0" : "px-5"}`}>
        <div className="h-9 w-9 rounded-lg bg-ai flex items-center justify-center shrink-0">
          <Sprout className="h-5 w-5 text-white" strokeWidth={2.4} />
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <div className="font-heading font-extrabold tracking-tight text-lg">
              Assort<span className="text-ai">IQ</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/50">Enterprise</div>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
        {NAV_GROUPS.map((group) => {
          const visible = group.items.filter((i) => (!i.perm || canUser(user, i.perm)) && !(i.excludeVerticals || []).includes(vertical));
          if (visible.length === 0) return null;
          return (
            <div key={group.label} className="space-y-1">
              {!collapsed && (
                <div className="px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-white/35">{group.label}</div>
              )}
              {visible.map((item) => {
                const locked = !!item.feature && !features.has(item.feature);
                return (
                  <NavRow key={item.to} item={{ ...item, label: nav(item.label) }} collapsed={collapsed} locked={locked}
                    onLocked={() => goUpgrade(item.feature)} />
                );
              })}
            </div>
          );
        })}

        <div className="space-y-1">
          {!collapsed && (
            <div className="px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-white/35">Account</div>
          )}
          {ACCOUNT_NAV.map((item) => (
            <NavRow key={item.to} item={item} collapsed={collapsed} />
          ))}
        </div>

        {admin && (
          <div className="space-y-1">
            {!collapsed && (
              <div className="px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-white/35 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Admin
              </div>
            )}
            {ADMIN_NAV.map((item) => (
              <NavRow key={item.to} item={item} collapsed={collapsed} />
            ))}
          </div>
        )}
      </nav>

      {trialing && !collapsed && (
        <div className="px-3 pb-4">
          <div data-testid="trial-banner" className="rounded-lg bg-ai/15 border border-ai/30 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-ai">Trial · {user?.days_left}d left</div>
            <button
              data-testid="sidebar-subscribe"
              onClick={() => goUpgrade()}
              className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-full bg-ai text-white py-1.5 text-[11px] font-bold uppercase tracking-wider hover:bg-[#e05400] transition-colors"
            >
              <Crown className="h-3 w-3" /> Upgrade plan
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}

export default SidebarNav;
